import { useCallback } from "react";
import { useStore } from "@shared/store/useStore";
import { DataProvider } from "@shared/constants/common";
import { GuestQuestionType } from "@shared/modules/basket/types/common/serializer-types";

export function useCustomQuestions(
  basketType: DataProvider,
  requireInfoCollection: boolean
) {
  // use useStore here to get basket fields
  // (prevents a dependency cycle from importing useBasket)
  const [basket] = useStore((state) => [state.getBasket(basketType)]);

  // returns if custom questions are on the basket
  // default value is basket.info_custom_question_data
  const hasCustomQuestions = useCallback(
    (
      customQuestionsData:
        | Array<GuestQuestionType>
        | undefined = basket?.info_custom_question_data
    ) => {
      return Boolean(customQuestionsData && customQuestionsData.length > 0);
    },
    [basket?.info_custom_question_data]
  );

  const isQuestionRequired = useCallback(
    (originalRequired: boolean): boolean => {
      if (basketType === DataProvider.Venue) {
        // Box office: use requireInfoCollection (i.e. enforceBoxOfficeInfoCollection) to override question.required
        return requireInfoCollection ? originalRequired : false;
      }
      // Public checkout: always use original question.required
      return originalRequired;
    },
    [basketType, requireInfoCollection]
  );

  // returns an array of formatted custom question objects
  // default value is basket.info_custom_question_data
  const getCustomQuestions = useCallback(
    (
      customQuestionsData:
        | Array<GuestQuestionType>
        | undefined = basket?.info_custom_question_data
    ): GuestQuestionType[] => {
      // update question requirement to include enforce_box_office_info_collection value
      // if enforce_box_office_info_collection is off then we don't enforce answering the question
      // to help speed up the box office checkout process
      return (
        customQuestionsData?.map((question, index) => {
          return {
            ...question,
            id: index.toString(),
            required: isQuestionRequired(question.required),
          };
        }) ?? []
      );
    },
    [basket?.info_custom_question_data]
  );

  return {
    hasCustomQuestions,
    getCustomQuestions,
  };
}
